import { useDispatch, useSelector } from 'react-redux';
import { useAlert } from 'src/hooks/useAlert';
import useLoading from 'src/hooks/useLoading';
import { AppDispatch, RootState } from 'src/redux';
import {
  getCreditLimitUser,
  getModelsUser,
  getUserkp,
  getDeviceData,
  getURLField,
  getPressignedUrl,
  getCreditLimitNewUser,
} from 'src/services/Onboarding/Users/user.service';
import {
  IGetUserParams,
  IPostModelNewUser,
} from 'src/services/Onboarding/Users/user.service.interface';

/**
 * Custom hook that provides functionalities to manage user data and credit limits.
 *
 * This hook utilizes Redux to fetch user information, credit limits, and device data,
 * while providing loading states and alerts for user actions.
 *
 * @returns {Object} An object containing:
 * - {Object} kpUser - The current keypago user data.
 * - {Object} responseCreditHistory - The response data for credit history requests.
 * - {Object} responseModelsUser - The response data for user model requests.
 * - {Object} deviceInfo - The device information related to the user.
 * - {function} getUserInfo - Fetches user information based on the provided parameters.
 * - {function} getCreditLimit - Fetches the credit limit for a user based on the provided parameters.
 * - {function} getAllModelsUsers - Fetches all user models based on the provided parameters.
 * - {function} getDeviceInfo - Fetches device information for a user based on the provided parameters.
 * - {function} downloadDocument - Downloads a document based on the provided file ID.
 * - {function} getCreditLimitNew - Fetches the credit limit for a new user based on the provided parameters.
 *
 * @throws Will show an alert if an error occurs during data fetching.
 */
const useUserkeypago = () => {
  const { kpUser, responseCreditHistory, responseModelsUser, deviceInfo, currentOnboardings } =
    useSelector((state: RootState) => state.kpUser);
  const dispatch = useDispatch<AppDispatch>();
  const { startLoading, stopLoading } = useLoading();
  const { showAlert } = useAlert();

  /**
   * Fetches user information based on the provided parameters.
   *
   * @param {IGetUserParams} payload - The parameters for fetching user information.
   * @returns {Promise<void>} A promise that resolves when the action is complete.
   */
  const getUserInfo = (payload: IGetUserParams) => {
    try {
      startLoading();
      dispatch(getUserkp(payload))
        .then((result: any) => {
          if (getUserkp.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al obtener la información del usuario',
            );
          } else if (getUserkp.fulfilled.match(result)) {
            showAlert(
              'success',
              'Información del usuario obtenida correctamente',
            );
          }
        })
        .finally(() => {
          stopLoading();
        });
    } catch (error) {
      showAlert('error', 'Error al obtener la información del usuario');
    }
  };

  /**
   * Fetches all user models based on the provided parameters.
   *
   * @param {IGetUserParams} payload - The parameters for fetching user models.
   * @returns {Promise<void>} A promise that resolves when the action is complete.
   */
  const getAllModelsUsers = (payload: IGetUserParams) => {
    try {
      startLoading();
      dispatch(getModelsUser(payload))
        .then((result: any) => {
          if (getModelsUser.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al obtener la información del usuario',
            );
          } else if (getModelsUser.fulfilled.match(result)) {
            showAlert(
              'success',
              'Información del usuario obtenida correctamente',
            );
          }
        })
        .finally(() => {
          stopLoading();
        });
    } catch (error) {
      showAlert('error', 'Error al obtener la información del usuario');
    }
  };

  /**
   * Fetches the credit limit for a user based on the provided parameters.
   *
   * @param {IGetUserParams} payload - The parameters for fetching the user's credit limit.
   * @returns {Promise<void>} A promise that resolves when the action is complete.
   */
  const getCreditLimit = (payload: IGetUserParams) => {
    try {
      startLoading();
      dispatch(getCreditLimitUser(payload))
        .then((result: any) => {
          if (getCreditLimitUser.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al obtener el historial de crédito del usuario',
            );
          } else if (getCreditLimitUser.fulfilled.match(result)) {
            showAlert(
              'success',
              'Historial de crédito del usuario obtenido correctamente',
            );
          }
        })
        .finally(() => {
          stopLoading();
        });
    } catch (error) {
      showAlert('error', 'Error al obtener el historial de crédito del usuario');
    }
  };

  /**
   * Fetches the credit limit for a new user based on the provided parameters.
   *
   * @param {IPostModelNewUser} payload - The parameters for fetching the new user's credit limit.
   * @returns {Promise<void>} A promise that resolves when the action is complete.
   */
  const getCreditLimitNew = (payload: IPostModelNewUser) => {
    try {
      startLoading();
      dispatch(getCreditLimitNewUser(payload))
        .then((result: any) => {
          if (getCreditLimitNewUser.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al obtener el historial de crédito del usuario',
            );
          } else if (getCreditLimitNewUser.fulfilled.match(result)) {
            showAlert(
              'success',
              'Historial de crédito del usuario obtenido correctamente',
            );
          }
        })
        .finally(() => {
          stopLoading();
        });
    } catch (error) {
      showAlert('error', 'Error al obtener el historial de crédito del usuario');
    }
  };

  /**
   * Fetches device information for a user based on the provided parameters.
   *
   * @param {IGetUserParams} payload - The parameters for fetching device information.
   * @returns {Promise<void>} A promise that resolves when the action is complete.
   */
  const getDeviceInfo = (payload: IGetUserParams) => {
    try {
      startLoading();
      dispatch(getDeviceData(payload))
        .then((result: any) => {
          if (getDeviceData.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al obtener el historial de crédito del usuario',
            );
          } else if (getDeviceData.fulfilled.match(result)) {
            showAlert(
              'success',
              'Historial de crédito del usuario obtenido correctamente',
            );
          }
        })
        .finally(() => {
          stopLoading();
        });
    } catch (error) {
      showAlert('error', 'Error al obtener el historial de crédito del usuario');
    }
  };

  /**
   * Downloads a document based on the provided file ID.
   *
   * @param {string} fileId - The ID of the file to download.
   * @returns {Promise<void>} A promise that resolves when the action is complete.
   */
  const downloadDocument = async (fileId: string) => {
    try {
      startLoading();
      const response = await getURLField(fileId);
      window.open(response.data.url);
      showAlert('success', 'Documento obtenido correctamente');
    } catch (error) {
      showAlert('error', 'Error al obtener el documento');
    } finally {
      stopLoading();
    }
  };

  return {
    kpUser,
    responseCreditHistory,
    responseModelsUser,
    deviceInfo,
    getUserInfo,
    getCreditLimit,
    getAllModelsUsers,
    getDeviceInfo,
    downloadDocument,
    getCreditLimitNew,
    currentOnboardings,
  };
};

export default useUserkeypago;
